import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../context/CompanyContext';
import { useToast } from '../../context/ToastContext';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import './Companies.css';

const Companies = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getAllCompanies, selectCompany, getAggregatedMetrics } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');

  const companies = getAllCompanies();
  const aggregated = getAggregatedMetrics();

  // Filter companies by search
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = [
    {
      label: 'Total Companies',
      value: companies.length.toString(),
      change: '+0',
      changeType: 'neutral'
    },
    {
      label: 'Total MRR',
      value: `$${aggregated.totalRevenue.toLocaleString()}`,
      change: '-',
      changeType: 'neutral'
    },
    {
      label: 'Total Users',
      value: aggregated.totalUsers.toLocaleString(),
      change: '-',
      changeType: 'neutral'
    },
    {
      label: 'Stripe Connected',
      value: `${aggregated.companiesWithStripe}/${companies.length}`,
      change: '-',
      changeType: aggregated.companiesWithStripe === companies.length ? 'positive' : 'warning'
    }
  ];

  const handleViewCompany = (company) => {
    selectCompany(company.id);
    navigate(`/companies/${company.id}`);
  };

  const handleConnectStripe = (company) => {
    showToast(`Initiating Stripe Connect for ${company.name}...`, 'info');
    // TODO: Implement Stripe Connect OAuth flow
  };

  const getStatusBadge = (company) => {
    if (company.stripeAccountId) {
      return <span className="status-badge connected">Stripe Connected</span>;
    }
    return <span className="status-badge pending">Needs Setup</span>;
  };

  return (
    <div className="companies-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Companies</h1>
          <p className="page-subtitle">Manage your portfolio of companies</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" icon="plus">Add Company</Button>
        </div>
      </div>

      <div className="stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      <div className="companies-section">
        <div className="section-header">
          <div className="search-box">
            <Icon name="search" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="companies-grid">
          {filteredCompanies.map((company) => (
            <div
              key={company.id}
              className="company-card"
              style={{ '--company-color': company.color }}
            >
              <div className="company-card-header">
                <div
                  className="company-logo"
                  style={{ background: company.color }}
                >
                  {company.logo}
                </div>
                <div className="company-info">
                  <h3 className="company-name">{company.name}</h3>
                  <span className="company-domain">{company.domain}</span>
                </div>
                {getStatusBadge(company)}
              </div>

              <p className="company-description">{company.description}</p>

              <div className="company-metrics">
                <div className="metric">
                  <span className="metric-label">MRR</span>
                  <span className="metric-value">
                    ${(company.metrics?.revenue?.mrr || 0).toLocaleString()}
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">Users</span>
                  <span className="metric-value">
                    {(company.metrics?.users?.total || 0).toLocaleString()}
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">Auth Events</span>
                  <span className="metric-value">
                    {(company.metrics?.auth?.logins || 0).toLocaleString()}
                  </span>
                </div>
                <div className="metric">
                  <span className="metric-label">Emails Sent</span>
                  <span className="metric-value">
                    {(company.metrics?.email?.sent || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="company-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleViewCompany(company)}
                >
                  View Dashboard
                </Button>
                {!company.stripeAccountId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="credit-card"
                    onClick={() => handleConnectStripe(company)}
                  >
                    Connect Stripe
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredCompanies.length === 0 && (
          <div className="companies-empty">
            <Icon name="building" />
            <h3>No companies found</h3>
            <p>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Add your first company to get started'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Companies;
